import { ArrowDown, ArrowLeft, ArrowUp, Code2, Copy, Eye, FileText, Inbox, Pencil, Plus, Save, Search, Trash2, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import Spinner from '../../components/Spinner';
import FormFieldModal from '../../components/lead-capture/FormFieldModal';
import { FormFieldRenderer } from '../../components/lead-capture/FormFieldRenderer';
import { useFormSubmissions, useLeadForm, useLeadFormMutations, useLeadForms } from '../../hooks/useLeadForms';
import { FormFieldApiInput, FormFieldType, FormSubmission, FormType, LeadForm, LeadFormField, LeadFormFieldInput } from '../../types/leadForms';
import { BrandingData, getBranding } from '../../api/brandingApi';
import { slugify } from '../../utils/slugify';
import { toast } from 'react-toastify';
import { FormDisclaimer } from './FormDisclaimer';

const formTypes = [
  { value: FormType.LeadCapture, label: 'Lead Capture' },
  { value: FormType.Newsletter, label: 'Newsletter' },
  { value: FormType.ContactUs, label: 'Contact Us' },
  { value: FormType.RequestDemo, label: 'Request Demo' },
  { value: FormType.RequestQuote, label: 'Request Quote' },
  { value: FormType.WebinarRegistration, label: 'Webinar Registration' },
  { value: FormType.EventRegistration, label: 'Event Registration' },
  { value: FormType.Feedback, label: 'Feedback' },
  { value: FormType.SupportRequest, label: 'Support Request' },
  { value: FormType.Custom, label: 'Custom' },
];

const colorFields = ['primaryColor', 'secondaryColor', 'buttonColor', 'accentColor'] as const;
const emptyForm: Omit<LeadForm, 'id' | 'updatedAt'> = {
  // brandingId: '',
  description: '',

  name: '',
  slug: '',

  formType: FormType.Custom,

  headerTitle: '',
  headerDescription: '',

  submitButtonText: 'Submit',
  successMessage: 'Thank you for your submission.',

  disclaimer: '',
  privacyPolicyUrl: '',

  primaryColor: '#172554',
  secondaryColor: '#e2e8f0',
  buttonColor: '#7f1d1d',
  accentColor: '#be123c',

  backgroundImageUrl: '',

  isActive: true,
  isPublic: true,

  fields: [],
};
const labelize = (value: string) =>
  value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/^./, (s) => s.toUpperCase());

const mapFieldToApi = (
  field: LeadFormFieldInput,
  displayOrder: number
): FormFieldApiInput => ({
  label: field.label,
  placeholder: field.placeholder ?? '',
  helpText: field.helpText ?? '',

  fieldType: field.type,
  isRequired: field.required,

  defaultValue: '',

  displayOrder,

  optionsJson: JSON.stringify(
    field.options ?? []
  ),

  minLength: null,
  maxLength: null,
});

const parseSubmission = (
  value: string
): Record<string, unknown> => {
  try {
    return JSON.parse(value);
  } catch {
    return {};
  }
};

const formatSubmissionValue = (
  value: unknown
): string => {
  if (Array.isArray(value)) {
    return value.join(', ');
  }

  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }

  if (
    value === null ||
    value === undefined ||
    value === ''
  ) {
    return '-';
  }

  return String(value);
};

const normalizeFields = (
  fields: LeadFormField[]
) =>
  fields.map((field, index) => ({
    ...field,
    displayOrder: index,
  }));

export function FormsPlatform() {
  return (
    <div className="space-y-6">
      

      <Routes>
        <Route index element={<FormsList />} />
        <Route path="new" element={<FormBuilder />} />
        <Route path=":id/edit" element={<FormBuilder />} />
        <Route path=":id/submissions" element={<FormSubmissions />}/>
      </Routes>
    </div>
  );
}

export function FormsList() {
  const [branding, setBranding] = useState<BrandingData | null>(null);
  const [shareForm, setShareForm] = useState<LeadForm | null>(null);

  useEffect(() => {
    const loadBranding = async () => {
      const data = await getBranding();
      setBranding(data);
    };

    loadBranding();
  }, []);
  
  const query = useLeadForms();
  const navigate = useNavigate();

  const { deleteForm } = useLeadFormMutations();

  if (query.isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (query.isError) {
    return (
      <p className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Unable to load forms.
      </p>
    );
  }

  const copyFormUrl = async (slug: string) => {
    const url = `${window.location.origin}/${slugify(
      branding?.companyName ?? 'company'
    )}/${slug}`;

    try {
      await navigator.clipboard.writeText(url);
      toast.success('Form link copied to clipboard');
    } catch {
      toast.error('Unable to copy form link');
    }
  };

  const forms = query.data ?? [];

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-strivre-navy flex items-center gap-2">
            <FileText size={22} />
            Forms
          </h1>

          <p className="text-sm text-strivre-mutedtext mt-1">
            Create and manage public forms used throughout the lead platform.
          </p>
        </div>

        <button
          type="button"
          onClick={() => navigate('/forms/new')}
          className="inline-flex items-center gap-2 bg-strivre-navy text-white rounded-lg px-4 py-2.5 text-sm font-semibold hover:bg-strivre-navy/90"
        >
          <Plus size={18} />
          Create Form
        </button>
      </div>
      <div className="overflow-x-auto rounded-lg border border-strivre-lightnavy bg-white">
        <table className="w-full min-w-[800px] text-left text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              {[
                'Name',
                'Form Type',
                'Slug',
                'Active',
                'Public',
                'Updated Date',
                'Actions',
              ].map((heading) => (
                <th
                  key={heading}
                  className="px-4 py-3"
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>

          <tbody className="divide-y divide-strivre-lightnavy">
            {forms.map((form) => (
              <tr
                key={form.id}
                className="hover:bg-slate-50 transition-colors"
              >
                <td className="px-4 py-3 font-semibold text-strivre-darktext">
                  {form.name}
                </td>

                <td className="px-4 py-3 text-strivre-darktext">
                  {formTypes.find(
                    (ft) => ft.value === form.formType
                  )?.label ?? 'Unknown'}
                </td>

                <td className="px-4 py-3 text-strivre-mutedtext">
                  {form.slug}
                </td>

                <td className="px-4 py-3 text-strivre-darktext">
                  {form.isActive ? 'Yes' : 'No'}
                </td>

                <td className="px-4 py-3 text-strivre-darktext">
                  {form.isPublic ? 'Yes' : 'No'}
                </td>

                <td className="px-4 py-3 text-strivre-mutedtext">
                  {form.updatedAt
                    ? new Date(form.updatedAt).toLocaleDateString()
                    : '-'}
                </td>

                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <Link
                      title="View Submissions"
                      to={`/forms/${form.id}/submissions`}
                      className="text-strivre-mutedtext hover:text-strivre-burgundy transition-colors"
                    >
                      <Inbox size={16} />
                    </Link>

                    <Link
                      title="Edit Form"
                      to={`/forms/${form.id}/edit`}
                      className="text-strivre-mutedtext hover:text-strivre-burgundy transition-colors"
                    >
                      <Pencil size={16} />
                    </Link>

                    <a
                      title="Preview Form"
                      href={`/${slugify(branding?.companyName ?? 'company')}/${form.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-strivre-mutedtext hover:text-strivre-burgundy transition-colors"
                    >
                      <Eye size={16} />
                    </a>

                    <button
                      type="button"
                      title="Copy Form Link"
                      onClick={() => copyFormUrl(form.slug)}
                      className="text-strivre-mutedtext hover:text-strivre-burgundy transition-colors"
                    >
                      <Copy size={16} />
                    </button>
                    <button
                      type="button"
                      title="Share & Embed"
                      onClick={() => setShareForm(form)}
                      className="text-strivre-mutedtext hover:text-strivre-burgundy transition-colors"
                    >
                      <Code2 size={16} />
                    </button>

                    <button
                      type="button"
                      title="Delete Form"
                      className="text-red-600 hover:text-red-700 transition-colors"
                      onClick={() => {
                        if (window.confirm(`Delete ${form.name}?`)) {
                          deleteForm.mutate(form.id);
                        }
                      }}
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}

            {forms.length === 0 && (
              <tr>
                <td
                  colSpan={7}
                  className="px-4 py-10 text-center"
                >
                  <div className="rounded-lg border border-dashed border-strivre-lightnavy bg-white p-10 text-center">
                    <p className="text-strivre-mutedtext">
                      No forms yet. Create your first form to get started.
                    </p>
                  </div>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {shareForm && (
        <FormShareModal
          form={shareForm}
          companyName={branding?.companyName}
          onClose={() => setShareForm(null)}
        />
      )}
    </div>
  );
}

function FormShareModal({
  form,
  companyName,
  onClose,
}: {
  form: LeadForm;
  companyName?: string;
  onClose: () => void;
}) {
  const baseUrl = window.location.origin;
  const companySlug = slugify(companyName ?? 'company');

  const publicUrl = `${baseUrl}/${companySlug}/${form.slug}`;
  const embedUrl = `${baseUrl}/${companySlug}/${form.slug}/embed`;
  const embedCode = `<iframe src="${embedUrl}" frameborder="0" width="100%" height="500px"></iframe>`;

  const copy = async (
    value: string,
    message: string
  ) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(message);
    } catch {
      toast.error('Unable to copy to clipboard');
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-strivre-navy/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
        onClick={(event) => event.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-strivre-lightnavy px-6 py-5">
          <div>
            <h2 className="text-xl font-bold text-strivre-navy">
              Share & Embed
            </h2>

            <p className="mt-1 text-sm text-strivre-mutedtext">
              {form.name}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-2 text-strivre-mutedtext transition-colors hover:bg-strivre-softgrey hover:text-strivre-burgundy"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-6 p-6">
          {/* Public URL */}
          <ShareField
            label="Public URL"
            description="Share this link directly with your customers."
            value={publicUrl}
            onCopy={() =>
              copy(
                publicUrl,
                'Form URL copied to clipboard'
              )
            }
          />

          {/* Embed URL */}
          <ShareField
            label="Embed URL"
            description="Use this URL when embedding the form in another website."
            value={embedUrl}
            onCopy={() =>
              copy(
                embedUrl,
                'Embed URL copied to clipboard'
              )
            }
          />

          {/* Embed Code */}
          <div>
            <div className="mb-2">
              <p className="text-sm font-bold text-strivre-darktext">
                Embed Code
              </p>

              <p className="mt-1 text-xs text-strivre-mutedtext">
                Paste this code into your website where you want the form to appear.
              </p>
            </div>

            <div className="relative">
              <pre className="max-h-40 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-strivre-lightnavy bg-strivre-softgrey p-4 pr-12 text-xs leading-relaxed text-strivre-darktext">
                {embedCode}
              </pre>

              <button
                type="button"
                title="Copy Embed Code"
                onClick={() =>
                  copy(
                    embedCode,
                    'Embed code copied to clipboard'
                  )
                }
                className="absolute right-2 top-2 rounded-lg border border-strivre-lightnavy bg-white p-2 text-strivre-mutedtext transition-colors hover:text-strivre-burgundy"
              >
                <Copy size={15} />
              </button>
            </div>
          </div>

          {/* Preview */}
          <div className="rounded-lg border border-strivre-lightnavy bg-strivre-softgrey p-4">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-strivre-lightburgundy p-2 text-strivre-burgundy">
                <Code2 size={18} />
              </div>

              <div>
                <p className="text-sm font-semibold text-strivre-darktext">
                  Embedding this form
                </p>

                <p className="mt-1 text-xs leading-relaxed text-strivre-mutedtext">
                  The embedded version displays the public form without the surrounding Strivre application interface.
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-end border-t border-strivre-lightnavy bg-strivre-softgrey px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="btn-secondary btn-md"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

function ShareField({
  label,
  description,
  value,
  onCopy,
}: {
  label: string;
  description: string;
  value: string;
  onCopy: () => void;
}) {
  return (
    <div>
      <div className="mb-2">
        <p className="text-sm font-bold text-strivre-darktext">
          {label}
        </p>

        <p className="mt-1 text-xs text-strivre-mutedtext">
          {description}
        </p>
      </div>

      <div className="flex items-start gap-2">
        <code className="min-w-0 flex-1 break-all rounded-lg border border-strivre-lightnavy bg-strivre-softgrey p-3 text-xs text-strivre-darktext">
          {value}
        </code>

        <button
          type="button"
          title={`Copy ${label}`}
          onClick={onCopy}
          className="rounded-lg border border-strivre-lightnavy p-2.5 text-strivre-mutedtext transition-colors hover:text-strivre-burgundy"
        >
          <Copy size={15} />
        </button>
      </div>
    </div>
  );
}

function Preview({
  form,
}: {
  form: Omit<LeadForm, 'id' | 'updatedAt'>;
}) {
  return (
    <div
      className="rounded-lg p-5"
      style={{
        backgroundColor: form.secondaryColor,
        backgroundImage: form.backgroundImageUrl
          ? `url(${form.backgroundImageUrl})`
          : undefined,
        backgroundSize: 'cover',
      }}
    >
      <div
        className="rounded-lg p-5 text-white"
        style={{
          backgroundColor: form.primaryColor,
        }}
      >
        <h3 className="text-xl font-bold">
          {form.headerTitle || 'Your Form Title'}
        </h3>

        <p className="mt-1 text-sm opacity-90">
          {form.headerDescription ||
            'Your form description will appear here.'}
        </p>
      </div>

      <div className="mt-3 space-y-4 rounded-lg bg-white p-4">
        {form.fields.map((field) => (
          <FormFieldRenderer
            key={field.id}
            field={field}
            accentColor={form.accentColor}
          />
        ))}

        {(form.disclaimer || form.privacyPolicyUrl) && (
          <FormDisclaimer
            disclaimer={form.disclaimer}
            privacyPolicyUrl={form.privacyPolicyUrl}
            accentColor={form.accentColor}
          />
        )}

        <button
          type="button"
          className="w-full rounded-md px-4 py-2 text-sm font-semibold text-white"
          style={{
            backgroundColor: form.buttonColor,
          }}
        >
          {form.submitButtonText || 'Submit'}
        </button>
      </div>
    </div>
  );
}

function FormSubmissions() {
  const { id } = useParams();
  const formQuery = useLeadForm(id);
  const submissionsQuery = useFormSubmissions(id);
  const [selectedSubmission, setSelectedSubmission] = useState<FormSubmission | null>(null);
  const [search, setSearch] = useState('');

  if (
    formQuery.isLoading ||
    submissionsQuery.isLoading
  ) {
    return (
      <div className="flex justify-center p-12">
        <Spinner />
      </div>
    );
  }

  if (
    formQuery.isError ||
    submissionsQuery.isError
  ) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        Unable to load form submissions.
      </div>
    );
  }

  const form = formQuery.data;
  const submissions = submissionsQuery.data ?? [];
  const fieldLabels = new Map(
    (form?.fields ?? []).map((field) => [
      field.id,
      field.label,
    ])
  );

  const filteredSubmissions = submissions.filter(
    (submission) => {
      if (!search.trim()) {
        return true;
      }

      return submission.submissionJson
        .toLowerCase()
        .includes(search.toLowerCase());
    }
  );

  return (
    <div className="space-y-5 text-left">
      <Link
        to="/forms"
        className="inline-flex items-center gap-2 text-sm font-medium text-strivre-mutedtext hover:text-strivre-burgundy"
      >
        <ArrowLeft size={16} />
        Back to Forms
      </Link>

      <div>
        <h1 className="flex items-center gap-2 text-2xl font-bold text-strivre-navy">
          <Inbox size={22} />
          {form?.name} Submissions
        </h1>

        <p className="mt-1 text-sm text-strivre-mutedtext">
          {submissions.length}{' '}
          {submissions.length === 1
            ? 'submission'
            : 'submissions'}
        </p>
      </div>
      <div className="rounded-lg border border-strivre-lightnavy bg-white p-4">
        <div className="relative">
          <Search
            size={17}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-strivre-mutedtext"
          />

          <input
            type="text"
            value={search}
            onChange={(e) =>
              setSearch(e.target.value)
            }
            placeholder="Search submissions..."
            className="input-base pl-10"
          />
        </div>
      </div>

      {submissions.length === 0 ? (
        <div className="rounded-lg border border-dashed border-strivre-lightnavy bg-white p-12 text-center">
          <Inbox
            size={34}
            className="mx-auto text-slate-300"
          />

          <p className="mt-3 text-sm text-strivre-mutedtext">
            No submissions have been received yet.
          </p>
        </div>
      ) : filteredSubmissions.length === 0 ? (
        <div className="rounded-lg border border-dashed border-strivre-lightnavy bg-white p-12 text-center">
          <Search
            size={34}
            className="mx-auto text-slate-300"
          />

          <p className="mt-3 text-sm text-strivre-mutedtext">
            No submissions match your search.
          </p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-lg border border-strivre-lightnavy bg-white">
          <table className="w-full text-left text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-3">
                  Submitted
                </th>

                {/* <th className="px-4 py-3">
                  Source
                </th> */}

                <th className="px-4 py-3">
                  Submission
                </th>

                <th className="px-4 py-3 text-right">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-strivre-lightnavy">
              {filteredSubmissions.map((submission: FormSubmission) => (
                <SubmissionRow
                  key={submission.id}
                  submission={submission}
                  fieldLabels={fieldLabels}
                  onView={() =>
                    setSelectedSubmission(submission)
                  }
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
      {selectedSubmission && (
        <SubmissionDetailsModal
          submission={selectedSubmission}
          fieldLabels={fieldLabels}
          onClose={() =>
            setSelectedSubmission(null)
          }
        />
      )}
    </div>
  );
}

function SubmissionDetailsModal({
  submission,
  fieldLabels,
  onClose,
}: {
  submission: FormSubmission;
  fieldLabels: Map<string, string>;
  onClose: () => void;
}) {
  const data = parseSubmission(
    submission.submissionJson
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-strivre-navy/40 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-strivre-lightnavy px-6 py-5">
          <div>
            <h2 className="text-xl font-bold text-strivre-navy">
              Submission Details
            </h2>

            <p className="mt-1 text-xs text-strivre-mutedtext">
              Submitted{' '}
              {new Date(
                submission.submittedDate
              ).toLocaleString()}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-2 text-strivre-mutedtext transition-colors hover:bg-strivre-softgrey hover:text-strivre-burgundy"
          >
            <X size={18} />
          </button>
        </div>

        {/* Submitted fields */}
        <div className="space-y-4 p-6">
          {Object.entries(data).map(
            ([fieldId, value]) => (
              <div
                key={fieldId}
                className="rounded-xl border border-slate-100 bg-slate-50 p-4"
              >
                <p className="text-xs font-semibold uppercase tracking-wide text-strivre-mutedtext">
                  {fieldLabels.get(fieldId) ??
                    'Field'}
                </p>

                {Array.isArray(value) ? (
                  <div className="mt-2 flex flex-wrap gap-2">
                    {value.map(
                      (item, index) => (
                        <span
                          key={`${fieldId}-${index}`}
                          className="rounded-full bg-strivre-lightburgundy px-3 py-1 text-xs font-semibold text-strivre-burgundy"
                        >
                          {String(item)}
                        </span>
                      )
                    )}
                  </div>
                ) : (
                  <p className="mt-1 break-words text-sm font-medium text-strivre-darktext">
                    {formatSubmissionValue(
                      value
                    )}
                  </p>
                )}
              </div>
            )
          )}
        </div>

        {/* Metadata */}
        {/* <div className="border-t border-strivre-lightnavy bg-slate-50 px-6 py-5">
          <p className="mb-3 text-xs font-bold uppercase tracking-wide text-strivre-mutedtext">
            Submission Information
          </p>

          <div className="space-y-2 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-strivre-mutedtext">
                Submission ID
              </span>

              <span className="break-all text-right font-medium text-strivre-darktext">
                {submission.id}
              </span>
            </div>

            {submission.visitorIp && (
              <div className="flex justify-between gap-4">
                <span className="text-strivre-mutedtext">
                  Visitor IP
                </span>

                <span className="font-medium text-strivre-darktext">
                  {submission.visitorIp}
                </span>
              </div>
            )}

            {submission.referrer && (
              <div className="flex justify-between gap-4">
                <span className="text-strivre-mutedtext">
                  Referrer
                </span>

                <span className="max-w-[70%] break-all text-right font-medium text-strivre-darktext">
                  {submission.referrer}
                </span>
              </div>
            )}
          </div>
        </div> */}
      </div>
    </div>
  );
}

function SubmissionRow({
  submission,
  fieldLabels,
  onView,
}: {
  submission: FormSubmission;
  fieldLabels: Map<string, string>;
  onView: () => void;
}) {
  const data =
    parseSubmission(
      submission.submissionJson
    );

  return (
    <tr onClick={onView} className="cursor-pointer transition-colors hover:bg-slate-50">
      <td className="px-5 py-4 align-top whitespace-nowrap">
        <p className="text-sm font-semibold text-strivre-darktext">
          {new Date(submission.submittedDate).toLocaleDateString()}
        </p>

        <p className="mt-0.5 text-xs text-strivre-mutedtext">
          {new Date(submission.submittedDate).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </p>
      </td>

      {/* <td className="px-4 py-4 text-strivre-mutedtext">
        {submission.source || '-'}
      </td> */}

      <td className="px-5 py-4 align-top">
        <div className="space-y-1">
          {Object.entries(data)
            .slice(0, 3)
            .map(([fieldId, value], index) => (
              <div
                key={fieldId}
                className={
                  index === 0
                    ? "font-semibold text-strivre-navy"
                    : "text-sm text-strivre-mutedtext"
                }
              >
                <span className="font-medium text-strivre-darktext">
                  {fieldLabels.get(fieldId) ?? 'Field'}:{' '}
                </span>

                <span className="text-strivre-mutedtext">
                  {formatSubmissionValue(value)}
                </span>

                {/* {formatSubmissionValue(value)} */}
              </div>
            ))}

          {Object.keys(data).length > 3 && (
            <p className="pt-1 text-xs font-medium text-strivre-burgundy">
              +{Object.keys(data).length - 3} more fields
            </p>
          )}
        </div>
      </td>

      <td className="px-5 py-4 text-right align-middle">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onView();
          }}
          title="View Submission"
          className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold text-strivre-burgundy transition-colors hover:bg-strivre-lightburgundy"
        >
          <Eye size={15} />
          View
        </button>
      </td>
    </tr>
  );
}

export function FormBuilder() {
  const { id } = useParams();
  const editing = Boolean(id);
  const navigate = useNavigate();
  const query = useLeadForm(id);
  const mutations = useLeadFormMutations();
  const [form, setForm] = useState<Omit<LeadForm, 'id' | 'updatedAt'>>(emptyForm);
  const [originalFields, setOriginalFields] = useState<LeadFormField[]>([]);
  const [field, setField] = useState<LeadFormField>();
  const [modalOpen, setModalOpen] = useState(false);
  
  useEffect(() => {
    if (!query.data) return;

    const {
      id: _id,
      updatedAt: _updatedAt,
      ...rest
    } = query.data;

    const fields = normalizeFields(
      (rest.fields ?? [])
        .sort(
          (a, b) =>
            (a.displayOrder ?? 0) -
            (b.displayOrder ?? 0)
        )
        .map((field) => ({
          id: field.id,

          label: field.label,
          placeholder: field.placeholder ?? '',
          helpText: field.helpText ?? '',

          type: field.fieldType,
          required: field.isRequired,

          options: field.optionsJson
            ? JSON.parse(field.optionsJson)
            : [],

          displayOrder: field.displayOrder,
        }))
    );

    setForm({
      ...emptyForm,
      ...rest,
      fields,
    });

    setOriginalFields(fields);
  }, [query.data]);

  const change = <K extends keyof typeof form
  >(
    key: K,
    value: (typeof form)[K]
  ) => {
    setForm((current) => ({
      ...current,
      [key]: value,
    }));
  };

  const saveForm = () => {
    const { fields, ...data } = form;

    const done = () => {
      navigate('/forms');
    };

    if (!editing) {
      mutations.createForm.mutate(data, {
        onSuccess: async (created) => {
          await Promise.all(
            fields.map(
              ({ id: _fieldId, ...fieldData }, index) =>
                mutations.createField.mutateAsync({
                  formId: created.id,
                  data: mapFieldToApi(
                    fieldData,
                    index
                  ),
                })
            )
          );

          done();
        },
      });

      return;
    }

    mutations.updateForm.mutate(
      {
        id: id!,
        data,
      },
      {
        onSuccess: async () => {
          const currentIds = new Set(
            fields
              .filter(
                (item) =>
                  !item.id.startsWith('local-')
              )
              .map((item) => item.id)
          );

          await Promise.all(
            originalFields
              .filter(
                (item) =>
                  !currentIds.has(item.id)
              )
              .map((item) =>
                mutations.deleteField.mutateAsync({
                  formId: id!,
                  fieldId: item.id,
                })
              )
          );

          await Promise.all(
            fields.map(
              ({ id: fieldId, ...fieldData }, index) =>
                fieldId.startsWith('local-')
                  ? mutations.createField.mutateAsync({
                      formId: id!,
                      data: mapFieldToApi(
                        fieldData,
                        index
                      ),
                    })
                  : mutations.updateField.mutateAsync({
                      formId: id!,
                      fieldId,
                      data: mapFieldToApi(
                        fieldData,
                        index
                      ),
                    })
            )
          );

          done();
        },
      }
    );
  };

  const saveField = (
    data: LeadFormFieldInput
  ) => {
    const localField: LeadFormField = {
      ...data,
      id:
        field?.id ??
        `local-${Date.now()}`,
    };

    setForm((current) => ({
      ...current,
      fields: normalizeFields(
        field
          ? current.fields.map((item) =>
              item.id === field.id
                ? localField
                : item
            )
          : [...current.fields, localField]
      ),
    }));

    setModalOpen(false);
  };

  const deleteField = (
    target: LeadFormField
  ) => {
    setForm((current) => ({
      ...current,
      fields: normalizeFields(
        current.fields.filter(
          (item) => item.id !== target.id
        )
      ),
    }));
  };
  
  const moveField = (
    target: LeadFormField,
    direction: 'up' | 'down'
  ) => {
    setForm((current) => {
      const index =
        current.fields.findIndex(
          (item) => item.id === target.id
        );

      const next =
        index +
        (direction === 'up' ? -1 : 1);

      if (
        index < 0 ||
        next < 0 ||
        next >= current.fields.length
      ) {
        return current;
      }

      const fields = [...current.fields];

      [fields[index], fields[next]] = [
        fields[next],
        fields[index],
      ];

      return {
        ...current,
        fields: normalizeFields(fields),
      };
    });
  };
  
  if (editing && query.isLoading) {
    return (
      <div className="flex justify-center p-12">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="space-y-5 text-left">
      <Link
        to="/forms"
        className="inline-flex items-center gap-2 text-sm font-medium text-strivre-mutedtext hover:text-strivre-burgundy"
      >
        <ArrowLeft size={16} />
        Back to Forms
      </Link>

      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-strivre-navy flex items-center gap-2">
            <FileText size={22} />
            {editing ? 'Edit Form' : 'Create Form'}
          </h1>

          <p className="text-sm text-strivre-mutedtext mt-1">
            Configure the form and preview it as you build.
          </p>
        </div>

        <button
          type="button"
          onClick={saveForm}
          disabled={
            mutations.createForm.isPending ||
            mutations.updateForm.isPending
          }
          className="btn-primary btn-md inline-flex items-center gap-2"
        >
          <Save size={16} />
          Save Form
        </button>
      </div>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_380px]">
        {/* Left Column */}
        <div className="space-y-5">
          {/* Form Settings */}
          <section className="rounded-lg border border-strivre-lightnavy bg-white p-5">
            <h3 className="mb-4 font-bold text-slate-900">
              Form Settings
            </h3>

            <div className="grid gap-4 md:grid-cols-2">
              {(
                [
                  'name',
                  'slug',
                  'headerTitle',
                  'headerDescription',
                  'submitButtonText',
                  'successMessage',
                  'backgroundImageUrl',
                ] as const
              ).map((key) => (
                <div
                  key={key}
                  className={
                    key === 'headerDescription' ||
                    key === 'successMessage'
                      ? 'md:col-span-2'
                      : ''
                  }
                >
                  <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">
                    {labelize(key)}
                  </label>

                  <input
                    className="input-base"
                    value={form[key]}
                    onChange={(e) =>
                      change(key, e.target.value)
                    }
                  />
                </div>
              ))}

              <div>
                <label className="block text-xs font-semibold text-strivre-mutedtext mb-1">
                  Form Type
                </label>

                <select
                  className="input-base font-normal"
                  value={form.formType}
                  onChange={(e) =>
                    change(
                      'formType',
                      Number(e.target.value) as FormType
                    )
                  }
                >
                  {formTypes.map((type) => (
                    <option
                      key={type.value}
                      value={type.value}
                    >
                      {type.label}
                    </option>
                  ))}
                </select>

              </div>
              <div className="md:col-span-2 border-t border-strivre-lightnavy pt-5">
                <div className="mb-4">
                  <h4 className="font-bold text-strivre-navy">
                    Submission Disclaimer
                  </h4>

                  <p className="mt-1 text-xs text-strivre-mutedtext">
                    Configure the information displayed to users before they submit this form.
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="mb-1 block text-xs font-semibold text-strivre-mutedtext">
                      Disclaimer
                    </label>

                    <textarea
                      rows={6}
                      className="input-base resize-y"
                      value={form.disclaimer ?? ''}
                      onChange={(e) =>
                        change('disclaimer', e.target.value)
                      }
                      placeholder="Enter the disclaimer displayed before the form is submitted."
                    />

                    <p className="mt-1 text-xs text-slate-500">
                      This text will appear above the Submit button.
                    </p>
                  </div>

                  <div>
                    <label className="mb-1 block text-xs font-semibold text-strivre-mutedtext">
                      Privacy Policy URL
                    </label>

                    <input
                      type="url"
                      className="input-base"
                      value={form.privacyPolicyUrl ?? ''}
                      onChange={(e) =>
                        change(
                          'privacyPolicyUrl',
                          e.target.value
                        )
                      }
                      placeholder="https://yourcompany.com/privacy"
                    />

                    <p className="mt-1 text-xs text-slate-500">
                      Users can review your Privacy Policy before submitting the form.
                    </p>
                  </div>
                </div>
              </div>
              <div className="md:col-span-2">
                <p className="mb-2 text-sm font-semibold text-slate-700">
                  Branding
                </p>

                <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  {colorFields.map((key) => (
                    <label
                      key={key}
                      className="flex items-center gap-2 text-xs font-semibold capitalize text-slate-600"
                    >
                      <input
                        type="color"
                        value={form[key]}
                        onChange={(e) =>
                          change(key, e.target.value)
                        }
                        className="h-9 w-10 cursor-pointer rounded border-0 p-0"
                      />

                      {labelize(
                        key.replace('Color', '')
                      )}
                    </label>
                  ))}
                </div>
              </div>

              <label className="flex items-center gap-2 self-end pb-2 text-sm font-semibold text-slate-600">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) =>
                    change(
                      'isActive',
                      e.target.checked
                    )
                  }
                />
                Active
              </label>

              <label className="flex items-center gap-2 text-sm font-semibold text-slate-600">
                <input
                  type="checkbox"
                  checked={form.isPublic}
                  onChange={(e) =>
                    change(
                      'isPublic',
                      e.target.checked
                    )
                  }
                />
                Public
              </label>
            </div>
          </section>

          {/* Fields */}
          <section className="rounded-lg border border-strivre-lightnavy bg-white p-5">
            <div className="mb-4 flex items-center justify-between">
              <h3 className="font-bold text-strivre-darktext">
                Fields
              </h3>

              <button
                type="button"
                onClick={() => {
                  setField(undefined);
                  setModalOpen(true);
                }}
                className="btn-secondary btn-md inline-flex items-center gap-2"
              >
                <Plus size={16} />
                Add Field
              </button>
            </div>

            <div className="space-y-2">
              {form.fields.map((item, index) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between rounded-md border border-strivre-lightnavy p-3"
                >
                  <div>
                    <p className="font-semibold text-strivre-darktext">
                      {item.label}
                      {item.required && ' *'}
                    </p>
                    <p className="text-xs text-strivre-mutedtext">
                      {FormFieldType[item.type]}
                    </p>
                  </div>

                  <div className="flex items-center gap-2 text-strivre-mutedtext">
                    <button
                      className="hover:text-strivre-burgundy transition-colors"
                      title="Move up"
                      type="button"
                      disabled={index === 0}
                      onClick={() =>
                        moveField(item, 'up')
                      }
                    >
                      <ArrowUp size={16} />
                    </button>

                    <button
                      className="hover:text-strivre-burgundy transition-colors"
                      title="Move down"
                      type="button"
                      disabled={
                        index ===
                        form.fields.length - 1
                      }
                      onClick={() =>
                        moveField(item, 'down')
                      }
                    >
                      <ArrowDown size={16} />
                    </button>

                    <button
                    className="hover:text-strivre-burgundy transition-colors"
                      title="Edit field"
                      type="button"
                      onClick={() => {
                        setField(item);
                        setModalOpen(true);
                      }}
                    >
                      <Pencil size={16} />
                    </button>

                    <button
                      title="Delete field"
                      type="button"
                      onClick={() =>
                        deleteField(item)
                      }
                      className="text-red-600"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              ))}

              {form.fields.length === 0 && (
                <p className="py-5 text-center text-sm text-strivre-mutedtext">
                  Add fields to start building this form.
                </p>
              )}
            </div>
          </section>
        </div>

        {/* Preview */}
        <aside className="space-y-5">
          <div className="sticky top-6 space-y-3">
            <h3 className="font-bold text-slate-900">
              Live Preview
            </h3>

            <Preview form={form} />
          </div>
        </aside>
      </div>

      {modalOpen && (
        <FormFieldModal
          field={field}
          onClose={() => setModalOpen(false)}
          onSave={saveField}
        />
      )}
    </div>
  );
}